import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bell,
  Sparkles,
  Activity,
  Shield,
  Zap,
  TrendingUp,
  Compass,
  ArrowLeftRight
} from 'lucide-react';
import { appDesignTokens as tokens } from './appDesignTokens';

const formatSweptDate = (iso) => {
  if (!iso) return 'Recently';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return 'Recently';
  return dt.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
};

const keyframesStyle = `
  @keyframes ping-slow {
    0% { transform: scale(1); opacity: 0.9; }
    70%, 100% { transform: scale(2.6); opacity: 0; }
  }
  @keyframes float-subtle {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
  }
  @keyframes pulse-subtle {
    0%, 100% { opacity: 0.95; }
    50% { opacity: 0.6; }
  }
  .watchlist-card {
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .watchlist-card:hover {
    transform: translateY(-4px);
    border-color: rgba(37, 99, 235, 0.25) !important;
    box-shadow: 0 12px 24px rgba(37, 99, 235, 0.05) !important;
    background: #ffffff !important;
  }
  @media (max-width: 720px) {
    .jobs-empty-shell {
      min-height: auto !important;
      padding: 12px 0 24px !important;
    }
    .jobs-empty-hero {
      padding: 18px 14px !important;
      border-radius: 14px !important;
    }
    .jobs-empty-title {
      font-size: 22px !important;
      line-height: 1.25 !important;
    }
    .jobs-empty-alert {
      padding: 14px 12px !important;
      gap: 12px !important;
    }
    .jobs-empty-alert-main {
      min-width: 0 !important;
    }
  }
`;

const PulseDot = ({ color = '#2563eb' }) => (
  <span style={{ position: 'relative', display: 'inline-flex', width: 8, height: 8, flexShrink: 0 }}>
    <span style={{
      position: 'absolute',
      display: 'inline-flex',
      height: '100%',
      width: '100%',
      borderRadius: '50%',
      backgroundColor: color,
      opacity: 0.85,
      animation: 'ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite'
    }} />
    <span style={{
      position: 'relative',
      display: 'inline-flex',
      borderRadius: '50%',
      width: 8,
      height: 8,
      backgroundColor: color
    }} />
  </span>
);

const JobsEmptyState = ({
  userName = 'there',
  variant = 'expired',
  watchedCompanies = [],
  lastSweptAt = null,
}) => {
  const navigate = useNavigate();
  const firstName = String(userName || 'there').split(/\s+/)[0];
  const isExpired = variant === 'expired';
  const needsProfile = variant === 'profile_needed';

  const subtitle = needsProfile
    ? 'Complete your profile and add your preferred roles to see personalized job matches.'
    : isExpired
      ? 'Your previous job batch has reached its natural expiration. To maintain strict alignment with active hiring trends and avoid stale listings, we are actively preparing your next batch.'
      : 'You have fully reviewed all recommendations in your current queue. Rather than flooding your feed with generic noise, we focus strictly on high-signal matching. We will alert you the moment a new role matches your parameters.';

  const companies = watchedCompanies.length ? watchedCompanies : [];

  const containerVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="jobs-empty-shell"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        fontFamily: tokens.fonts.body,
        padding: '24px 0 40px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 120px)',
      }}
    >
      <style>{keyframesStyle}</style>

      {/* Hero Concierge Header */}
      <motion.div
        variants={itemVariants}
        className="jobs-empty-hero"
        style={{
          background: 'linear-gradient(135deg, rgba(37,99,235,0.03) 0%, rgba(37,99,235,0.01) 100%)',
          borderRadius: 20,
          border: '1px solid rgba(37,99,235,0.08)',
          padding: '28px 32px',
          marginBottom: 24,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 200,
          height: '100%',
          background: 'radial-gradient(circle at top right, rgba(37,99,235,0.06) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: tokens.radius.pill,
              background: 'rgba(37,99,235,0.06)',
              border: '1px solid rgba(37,99,235,0.12)',
            }}>
              <PulseDot color={tokens.colors.accent} />
              <span style={{
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: tokens.colors.accentStrong,
                textTransform: 'uppercase'
              }}>
                Looking For Matches...
              </span>
            </div>

            <div style={{
              fontSize: 11.5,
              fontWeight: 600,
              color: tokens.colors.textMuted,
              display: 'flex',
              alignItems: 'center',
              gap: 5
            }}>
              <Activity size={12} color={tokens.colors.textDim} style={{ animation: 'pulse-subtle 1.8s infinite' }} />
              Last checked · <strong style={{ color: tokens.colors.textSecondary }}>{formatSweptDate(lastSweptAt)}</strong>
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: needsProfile ? 'center' : 'flex-start',
            justifyContent: needsProfile ? 'center' : 'flex-start',
            flexDirection: needsProfile ? 'column' : 'row',
            textAlign: needsProfile ? 'center' : 'left',
            gap: 20,
            marginTop: 4
          }}>
            <div style={{
              width: 52,
              height: 52,
              borderRadius: 16,
              background: '#ffffff',
              border: '1px solid rgba(37,99,235,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 8px 16px rgba(37,99,235,0.03)',
              animation: 'float-subtle 3s ease-in-out infinite'
            }}>
              <Sparkles size={24} color={tokens.colors.accent} />
            </div>

            <div style={{ flex: 1 }}>
              <h2 style={{
                margin: '0 0 10px',
                fontSize: 28,
                lineHeight: 1.2,
                fontWeight: 800,
                color: tokens.colors.textPrimary,
                fontFamily: tokens.fonts.body,
                letterSpacing: '-0.02em'
              }}
              className="jobs-empty-title"
              >
                {needsProfile ? 'Before we match jobs, ' : 'You&apos;re completely caught up, '}
                <span style={{
                  fontStyle: 'italic',
                  color: tokens.colors.accent,
                  background: `linear-gradient(120deg, ${tokens.colors.accent}, ${tokens.colors.accentStrong})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>{firstName}.</span>
              </h2>
              <p style={{
                margin: 0,
                fontSize: 14.5,
                color: tokens.colors.textSecondary,
                lineHeight: 1.6,
                maxWidth: 580
              }}>
                {subtitle}
              </p>
              {needsProfile && (
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  style={{
                    marginTop: 14,
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: 'none',
                    background: tokens.colors.accent,
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Complete Profile & Add Open-to Roles
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>



      {/* Elegant Alerts Card */}
      <motion.div
        variants={itemVariants}
        className="jobs-empty-alert"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
          padding: '20px 24px',
          borderRadius: 20,
          background: `linear-gradient(135deg, ${tokens.colors.surface} 0%, rgba(37,99,235,0.02) 100%)`,
          border: `1px solid ${tokens.colors.border}`,
          marginBottom: 28,
          boxShadow: '0 10px 30px rgba(37, 99, 235, 0.03)',
          color: tokens.colors.textPrimary,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 10% 20%, rgba(37,99,235,0.03) 0%, transparent 60%)',
          pointerEvents: 'none'
        }} />

        <div className="jobs-empty-alert-main" style={{ display: 'flex', gap: 16, alignItems: 'center', flex: 1, minWidth: 260 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'rgba(37, 99, 235, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: tokens.colors.accent,
            flexShrink: 0
          }}>
            <Bell size={20} />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15.5, letterSpacing: '-0.01em', color: tokens.colors.textPrimary }}>
              Personalized Job Alerts Active
            </div>
            <div style={{ fontSize: 12, color: tokens.colors.textSecondary, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>We’ll notify you when matching jobs are available.</span>
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '8px 16px',
            borderRadius: tokens.radius.pill,
            background: 'rgba(37, 99, 235, 0.06)',
            border: `1px solid rgba(37, 99, 235, 0.15)`,
            color: tokens.colors.accent,
            fontWeight: 800,
            fontSize: 12,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.02)'
          }}
        >
          <Zap size={12} fill={tokens.colors.accent} />
          Notification On
        </div>
      </motion.div>

      {/* Premium Assistant Guarantee Footer */}
      <motion.div
        variants={itemVariants}
        style={{
          marginTop: 40,
          paddingTop: 20,
          borderTop: `1px solid ${tokens.colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: 'rgba(5,150,105,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: tokens.colors.success
          }}>
            <Shield size={14} />
          </div>
          <p style={{ margin: 0, fontSize: 12, color: tokens.colors.textMuted, fontWeight: 500 }}>
            Only relevant jobs. No random spam.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/profile')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: 12,
            fontWeight: 700,
            color: tokens.colors.textSecondary,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4
          }}
          onMouseEnter={e => e.currentTarget.style.color = tokens.colors.accent}
          onMouseLeave={e => e.currentTarget.style.color = tokens.colors.textSecondary}
        >
          Edit Preferences
          <ArrowLeftRight size={12} />
        </button>
      </motion.div>
    </motion.div>
  );
};

export default JobsEmptyState;
